import { useEffect, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import { BrandSection } from "./Sponsorship";
import {
  SPONSOR_TIERS, SPONSOR_RACK_CR, TIER_BY_ID, effectiveValue, paidTotal, paymentStatus,
} from "../lib/constants";
import { formatINR, formatPercent, formatDateShort } from "../lib/format";
import { exportCSV } from "../lib/csv";
import { Download } from "lucide-react";

export default function Sponsors() {
  const [rows, setRows] = useState([]);

  const reload = async () => {
    const { data } = await api.get("/sponsorships?kind=sponsor");
    setRows(data);
  };
  useEffect(() => { reload(); }, []);

  const closed = rows.filter((r) => r.status === "closed").reduce((s, r) => s + effectiveValue(r), 0);
  const negotiating = rows.filter((r) => r.status === "negotiating").length;
  const prospect = rows.filter((r) => r.status === "prospect").length;
  const closedRows = rows.filter((r) => r.status === "closed");
  const received = closedRows.reduce((s, r) => s + paidTotal(r), 0);
  const outstanding = Math.max(0, closed - received);
  const overdueCount = closedRows.filter((r) => paymentStatus(r) === "overdue").length;

  const onExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    exportCSV(`isf-sponsors-${stamp}.csv`, rows, [
      { label: "Brand",        accessor: (r) => r.brand_name },
      { label: "Tier",         accessor: (r) => TIER_BY_ID[r.tier]?.label ?? r.tier },
      { label: "Rack ₹ Cr",    accessor: (r) => (r.value_cr * r.slots_taken).toFixed(2) },
      { label: "Closing ₹ Cr", accessor: (r) => (r.closing_price_cr ?? "") === "" ? "" : Number(r.closing_price_cr).toFixed(2) },
      { label: "Slots",        accessor: (r) => r.slots_taken },
      { label: "Status",       accessor: (r) => r.status },
      { label: "Wave",         accessor: (r) => `W${r.wave}` },
      { label: "Notes",        accessor: (r) => r.notes || "" },
      { label: "Created",      accessor: (r) => formatDateShort(r.created_at) },
      { label: "Updated",      accessor: (r) => formatDateShort(r.updated_at) },
    ]);
  };

  return (
    <Layout>
      <PageHeader
        overline="Pipeline · sponsor tiers"
        title="Sponsors"
        subtitle="Title, Powered, Co-Powered & Associate partners — ₹ 12 Cr sponsor rack."
        actions={
          <button
            onClick={onExport}
            disabled={rows.length === 0}
            data-testid="export-sponsors-csv"
            className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] disabled:opacity-40 disabled:cursor-not-allowed text-sm px-4 py-2 transition-colors"
            title={rows.length === 0 ? "No rows to export" : `Export ${rows.length} sponsors to CSV`}
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        }
        testId="sponsors-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="p-5" testId="kpi-sponsor-closed">
          <div className="overline mb-2">Closed</div>
          <div className="font-display text-2xl font-bold tabular">{formatINR(closed)}</div>
          <div className="text-xs text-zinc-500 mt-1">of {formatINR(SPONSOR_RACK_CR)} · {formatPercent(closed, SPONSOR_RACK_CR)}</div>
          <div className="mt-3 h-1 bg-zinc-900">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (closed / SPONSOR_RACK_CR) * 100)}%` }} />
          </div>
        </Card>
        <Card className="p-5" testId="kpi-sponsor-received">
          <div className="overline mb-2">Received</div>
          <div className="font-display text-2xl font-bold tabular text-emerald-400">{formatINR(received)}</div>
          <div className="text-xs text-zinc-500 mt-1">{closed > 0 ? `${((received / closed) * 100).toFixed(0)}% of closed` : "—"}</div>
        </Card>
        <Card className="p-5" testId="kpi-sponsor-outstanding">
          <div className="overline mb-2">Outstanding</div>
          <div className="font-display text-2xl font-bold tabular text-amber-400">{formatINR(outstanding)}</div>
          <div className="text-xs text-zinc-500 mt-1">unpaid balance</div>
        </Card>
        <Card className={`p-5 ${overdueCount > 0 ? "border-red-500/30" : ""}`} testId="kpi-sponsor-overdue">
          <div className="overline mb-2">Overdue</div>
          <div className={`font-display text-2xl font-bold tabular ${overdueCount > 0 ? "text-red-400" : ""}`}>{overdueCount}</div>
          <div className="text-xs text-zinc-500 mt-1">closed but unpaid 30+ days</div>
        </Card>
        <Card className="p-5" testId="kpi-sponsor-negotiating">
          <div className="overline mb-2">Negotiating</div>
          <div className="font-display text-2xl font-bold tabular">{negotiating}</div>
          <div className="text-xs text-zinc-500 mt-1">in conversation</div>
        </Card>
        <Card className="p-5" testId="kpi-sponsor-prospect">
          <div className="overline mb-2">Prospects</div>
          <div className="font-display text-2xl font-bold tabular">{prospect}</div>
          <div className="text-xs text-zinc-500 mt-1">leads in pipeline</div>
        </Card>
      </div>

      <BrandSection
        kind="sponsor"
        tierOptions={SPONSOR_TIERS}
        totalRack={SPONSOR_RACK_CR}
        allRows={rows}
        reload={reload}
      />
    </Layout>
  );
}
